// Organization audit trail (administrators). Server-paginated, newest first.
import type { ColumnDef } from "@tanstack/react-table";

import { FileClock } from "lucide-react";
import { useState } from "react";
import { api, call } from "@/api/client";
import { usePaged, useMe } from "@/api/hooks";
import type { AuditEvent } from "@/api/types";
import { formatDateTime, shortId } from "@/lib/format";
import { auditActionLabel, auditEntityLabel } from "@/lib/labels";
import { Field } from "@/ui/field";
import { NativeSelect } from "@/ui/input";
import { EmptyState, PageHeader } from "@/ui/layout";
import { DataTable } from "@/components/data-table";
import { PageError, useTitle, useRouteParams } from "../common/page";

const ENTITY_FILTERS = [
  "",
  "client",
  "brand",
  "campaign",
  "creative",
  "creative_version",
  "distribution",
  "serving_hold",
  "membership",
  "invitation",
];

export function AuditPage() {
  const { orgId } = useRouteParams("orgId");
  const { data: me } = useMe();
  const [entityType, setEntityType] = useState("");
  const events = usePaged(["org", orgId, "audit", entityType], (cursor) =>
    call(() =>
      api.GET("/orgs/{orgId}/audit", {
        params: {
          path: { orgId },
          query: { limit: 50, ...(cursor ? { cursor } : {}), ...(entityType ? { entityType } : {}) },
        },
      })
    )
  );
  useTitle("Auditoría");
  if (events.error) return <PageError error={events.error} orgId={orgId} />;
  const columns: Array<ColumnDef<AuditEvent>> = [
    {
      header: "Fecha",
      cell: ({ row }) => <span className="tabular whitespace-nowrap">{formatDateTime(row.original.occurredAt)}</span>,
    },
    { header: "Elemento", cell: ({ row }) => auditEntityLabel(row.original.entityType) },
    { header: "Evento", cell: ({ row }) => auditActionLabel(row.original.action) },
    {
      header: "Por",
      cell: ({ row }) => {
        const actor = row.original.actor;
        if (actor.userId && actor.userId === me?.user.id) return "Tú";
        if (actor.userId) return `Usuario …${shortId(actor.userId)}`;
        return actor.system ? "Sistema" : "—";
      },
    },
  ];
  return (
    <div className="grid gap-6">
      <PageHeader title="Auditoría" description="Quién cambió qué y cuándo en este espacio de trabajo." />
      <div className="max-w-xs">
        <Field label="Tipo de elemento">
          <NativeSelect
            value={entityType}
            onChange={(e) => {
              setEntityType(e.target.value);
            }}
          >
            {ENTITY_FILTERS.map((t) => (
              <option key={t} value={t}>
                {t === "" ? "Todos" : auditEntityLabel(t)}
              </option>
            ))}
          </NativeSelect>
        </Field>
      </div>
      <DataTable
        label="Eventos de auditoría"
        columns={columns}
        rows={events.items}
        loading={events.isLoading}
        hasMore={events.hasNextPage}
        loadingMore={events.isFetchingNextPage}
        onLoadMore={() => void events.fetchNextPage()}
        empty={<EmptyState icon={<FileClock />} title="Sin eventos" />}
      />
    </div>
  );
}
